import { Link } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button, buttonVariants } from "@/components/ui/button";
import type { Invoice } from "@/lib/invoice.api";
import { InvoicePdfButton } from "@/pages/documents/invoices/components/InvoicePdfButton";
import { cn } from "@/lib/utils";
import {
  DocumentEmptyState,
  DocumentLineCard,
  fmtDate,
  fmtDateTime,
  money,
} from "@/pages/documents/components/DocumentWorkspace";
import { getInvoiceLabel } from "../invoice-formatters";
import { InvoiceMiniStat } from "./InvoiceMiniFacts";
import { InvoiceStatusBadges } from "./InvoiceStatusBadges";

interface InvoiceQuickViewDialogProps {
  invoice: Invoice | null;
  onOpenChange: (open: boolean) => void;
}

export function InvoiceQuickViewDialog({ invoice, onOpenChange }: InvoiceQuickViewDialogProps) {
  return (
    <Dialog open={Boolean(invoice)} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[88dvh] overflow-y-auto bg-card sm:max-w-3xl">
        {invoice && (
          <>
            <DialogHeader>
              <div className="flex flex-col gap-3 pr-8 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <DialogTitle className="text-[17px] font-bold text-foreground">
                    {getInvoiceLabel(invoice)}
                  </DialogTitle>
                  <DialogDescription className="mt-1 text-[12px] text-muted-foreground">
                    {invoice.partySnapshot?.fullName ?? "Client non renseigné"} / créée le{" "}
                    {fmtDateTime(invoice.createdAt)}
                  </DialogDescription>
                </div>
                <InvoiceStatusBadges invoice={invoice} />
              </div>
            </DialogHeader>

            <div className="grid gap-3 sm:grid-cols-4">
              <InvoiceMiniStat label="Total" value={money(invoice.totalAmount)} />
              <InvoiceMiniStat label="Lignes" value={String(invoice.lines.length)} />
              <InvoiceMiniStat label="Émission" value={fmtDate(invoice.issuedAt ?? invoice.createdAt)} />
              <InvoiceMiniStat label="Échéance" value={fmtDate(invoice.dueDate)} />
            </div>

            <div className="overflow-hidden rounded-lg border border-border">
              <div className="border-b border-border bg-surface-muted px-4 py-2">
                <p className="text-[10.5px] font-semibold uppercase tracking-wide text-muted-foreground">
                  Contenu de la facture
                </p>
              </div>
              <div className="divide-y divide-neutral-100">
                {invoice.lines.length === 0 ? (
                  <div className="p-3">
                    <DocumentEmptyState
                      title="Aucune ligne"
                      description="Cette facture ne contient aucun service."
                    />
                  </div>
                ) : (
                  invoice.lines.map((line) => (
                    <div key={line.id} className="p-3">
                      <DocumentLineCard line={line} />
                    </div>
                  ))
                )}
              </div>
            </div>

            <DialogFooter>
              <Button variant="secondary" onClick={() => onOpenChange(false)}>
                Fermer
              </Button>
              {invoice.status === "issued" && (
                <InvoicePdfButton invoiceId={invoice.id}>PDF</InvoicePdfButton>
              )}
              <Link to={`/invoices/${invoice.id}`} className={cn(buttonVariants())}>
                Voir le détail
              </Link>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
